import { Box, Divider, Typography } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { SCREENS, UrlConstants } from "../../utils/CommonUtils/constants";
import {
  getAudioSaveSuccessDialogBtnText,
  getAudioSaveSuccessDialogDescription,
} from "../../utils/helper";
import { getAudioSaveSuccessDialogImage } from "../../utils/assessmentStepVisuals";
import * as Sentry from "@sentry/react";
import useTranslationSetup from "../../utils/useTranslationSetup";
import AssessmentHeader from "../../components/assessmentHeader/AssessmentHeader";
import useAudioRecorderV3 from "../../utils/useAudioRecorderV3";
import AssessmentFooter from "../../components/assessmentFooter/AssessmentFooter";
import InstructionBackdrop from "../../components/instructionBackdrop/InstructionBackdrop";
import RetakeAudioDialog from "../../components/retakeAudioDialog/RetakeAudioDialog";
import SaveAudioConfirmationDrawer from "../../components/saveAudioConfirmationDrawer/SaveAudioConfirmationDrawer";
import LoadingDialog from "../../components/LoadingDialog/LoadingDialog";
import useQualityCheck from "../../hooks/useQualityCheck";
import AudioSizeShortError from "../../components/AudioSizeShortError/AudioSizeShortError";
import AudioSaveSuccessDialog from "../../components/audioSaveSuccessDialog/AudioSaveSuccessDialog";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CustomErrorDialogBox from "../../components/CustomErrorDialogBox/CustomErrorDialogBox";
import ReadingDifficultyConfirmationDrawer from "../../components/readingDifficultyConfirmationDrawer/ReadingDifficultyConfirmationDrawer";
import getUploadPresignedUrl from "../../fetchApi/getUploadPresignedUrl";
import uploadAudioUsingPresignedUrl from "../../fetchApi/uploadAudioUsingPresignedUrl";
import processAssessment from "../../fetchApi/processAssessment";
import { toAudioQualityPayload } from "../../utils/audioQuality/toAudioQualityPayload";
import useParamValues from "../../utils/useParamValues";
import useWebViewBridge from "../../utils/useWebViewBridge";
import allAssessmentUploaded from "../../fetchApi/allAssessmentAudioUploaded";
import WordDisplayBox from "../../components/WordBoxDisplay/WordDisplayBox";
import "./styles.css";

const AssessmentScreen = ({
  currentStep,
  setCurrentStep,
  createAssessmentResponse,
  setActiveScreen,
}) => {
  const {
    isRecording,
    isStopped,
    isProcessing,
    audioBlob,
    setAudioBlob,
    startRecording,
    stopRecording,
  } = useAudioRecorderV3();
  const { t, activeLanguage } = useTranslationSetup();
  const { studentName, token } = useParamValues();
  const { sendMessageToReactNative } = useWebViewBridge();
  const [fontSize, setFontSize] = useState(7.5); // Initial font size of 7.5vw
  const [duration, setDuration] = useState(0);
  const [audioBlobRef, setAudioBlobRef] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [showBackButton, setShowBackButton] = useState(true);
  const [showSlider, setShowSlider] = useState(true);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [openRetakeAudioDialog, setOpenRetakeAudioDialog] = useState(false);
  const [openSaveAudioConfirmationDrawer, setOpenSaveAudioConfirmationDrawer] =
    useState(false);
  const {
    result: qualityResult,
    isChecking: isQualityChecking,
    runQualityCheck,
    reset: resetQualityCheck,
  } = useQualityCheck();
  const [
    openReadingDifficultyConfirmationDrawer,
    setOpenReadingDifficultyConfirmationDrawer,
  ] = useState(false);
  const [audioSizeShort, setAudioSizeShort] = useState(false);
  const [audioSaveSuccessDialog, setAudioSaveSuccessDialog] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const [audioUploadStatus, setAudioUploadStatus] = useState(null);
  const [allAssessmentUploadedLoading, setAllAssessmentUploadedLoading] =
    useState(false);
  const [allAssessmentAudioStatus, setAllAssessmentAudioStatus] =
    useState(null);
  const assessmentUrl = `${
    UrlConstants.BASE_URL + UrlConstants.BASE_PATH_URL + UrlConstants.ASSESSMENT
  }`;
  const firstName = studentName ? studentName.split(" ")[0] : "";
  const abortController = useRef(null);

  async function handleAudioUpload(blob, audioQualityResult) {
    try {
      const preSignedUrl = await getUploadPresignedUrl(
        `${assessmentUrl}/${createAssessmentResponse?.id}/upload-presigned-url`,
        abortController,
        token
      );
      await uploadAudioUsingPresignedUrl(preSignedUrl, blob, abortController);

      await processAssessment(
        `${assessmentUrl}/${
          createAssessmentResponse?.id
        }/process-assessment?audio_type=${blob.type}`,
        abortController,
        token,
        toAudioQualityPayload(audioQualityResult)
      );
      setAudioUploading(false);
      setAudioUploadStatus("COMPLETED");
    } catch (error) {
      Sentry.captureException(error);
      setAudioUploading(false);
      setAudioUploadStatus("ERROR");
      setOpenSaveAudioConfirmationDrawer(false);
    }
  }

  async function handleAllAssessmentUploaded() {
    try {
      await allAssessmentUploaded(
        `${assessmentUrl}/${createAssessmentResponse?.primary_assessment_id}/all-uploaded`,
        abortController,
        token
      );
      setAllAssessmentUploadedLoading(false);
      setAllAssessmentAudioStatus("COMPLETED");
    } catch (error) {
      Sentry.captureException(error);
      setAllAssessmentUploadedLoading(false);
      setAllAssessmentAudioStatus("ERROR");
    }
  }

  const handleStartRecording = () => {
    setOpenBackdrop(true);
    setTimeout(() => {
      if (!openBackdrop) {
        setShowSlider(false);
        setShowBackButton(false);
        startRecording();
      }
    }, 5500);
  };
  const handleStopRecording = () => {
    if (duration === undefined || (duration !== null && duration <= 5)) {
      setAudioSizeShort(true);
    }
    setShowBackButton(true);
    stopRecording();
  };
  const onClickAudioRecorder = () => {
    if (isRecording) {
      handleStopRecording();
    } else {
      handleStartRecording();
    }
  };
  const handleAudioSizeErrorRetake = () => {
    setAudioSizeShort(false);
    setShowSlider(true);
    setAudioBlob(null);
  };
  const handleRetakeAudioRetake = () => {
    setOpenRetakeAudioDialog(false);
    setShowSlider(true);
    setAudioBlob(null);
  };

  const handleSaveClick = async () => {
    if (!audioBlob) return;
    await runQualityCheck(audioBlob);
    setOpenSaveAudioConfirmationDrawer(true);
  };

  const handleSaveRecording = () => {
    setAudioUploading(true);
    handleAudioUpload(audioBlob, qualityResult);
  };

  const handleRetakeRecording = () => {
    resetQualityCheck();
    setShowSlider(true);
    setAudioBlob(null);
  };

  const handleRetryClick = () => {
    setErrorDialogOpen(false);
    setAudioUploadStatus(null);
    setAllAssessmentAudioStatus(null);
  };

  const onClickAudioSaveSuccess = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
      setActiveScreen(SCREENS.RENDER_ASSESSMENT_LOADING_SCREEN);
    } else if (currentStep === 2) {
      if (openReadingDifficultyConfirmationDrawer) {
        sendMessageToReactNative("status=0");
        return;
      }
      setAudioSaveSuccessDialog(false);
      setOpenSaveAudioConfirmationDrawer(false);
      setAudioUploadStatus(null);
      setOpenReadingDifficultyConfirmationDrawer(true);
    } else {
      sendMessageToReactNative("status=0");
    }
  };

  const handleReadingDifficultyConfirmationSecondary = () => {
    setCurrentStep(3);
    setActiveScreen(SCREENS.RENDER_ASSESSMENT_LOADING_SCREEN);
  };
  const handleReadingDifficultyConfirmationPrimary = () => {
    setAllAssessmentAudioStatus(null);
    setAllAssessmentUploadedLoading(true);
    handleAllAssessmentUploaded();
  };

  useEffect(() => {
    if (audioBlob !== null && duration <= 5) {
      setAudioSizeShort(true);
    }
  }, [duration, audioBlob]);

  useEffect(() => {
    if (audioUploadStatus === "ERROR" || allAssessmentAudioStatus === "ERROR") {
      setErrorDialogOpen(true);
    }
  }, [audioUploadStatus, allAssessmentAudioStatus]);
  useEffect(() => {
    if (audioUploadStatus === "COMPLETED") {
      if (currentStep === 3) {
        setAllAssessmentUploadedLoading(true);
        handleAllAssessmentUploaded();
      } else {
        setAudioSaveSuccessDialog(true);
      }
    }
  }, [currentStep, audioUploadStatus]);
  useEffect(() => {
    if (allAssessmentAudioStatus === "COMPLETED") {
      // if (currentStep === 2) {
      //   sendMessageToReactNative("status=0");
      // } else if (currentStep === 3) {
      //   setAudioSaveSuccessDialog(true);
      // }
      setAudioSaveSuccessDialog(true);
    }
  }, [currentStep, allAssessmentAudioStatus]);

  return (
    <>
      <Box>
        <AssessmentHeader
          heading={`${t("student_orf_result", {
            firstName: firstName,
          })}`}
          onClick={() => {}}
        />
        <Divider className="assessment-screen-divider" />

        <Box className="assessment-container">
          <Box className="assessment-paragraph-wrapper">
            {showSlider && (
              <Box className="assessment-screen-text-overlay">
                <Typography className="assessment-screen-text-overlay-item">
                  {t("assessment_screen_paragraph_text_overlay")}
                </Typography>
              </Box>
            )}
            <Typography
              fontSize={`${fontSize}vw`}
              className={`assessment-paragraph ${showSlider ? "blur" : ""}`}
              lang={activeLanguage}
            >
              {createAssessmentResponse?.content_type === "words" ? (
                <WordDisplayBox
                  text={createAssessmentResponse?.paragraph}
                  fontSize={fontSize}
                />
              ) : (
                createAssessmentResponse?.paragraph
              )}
            </Typography>
          </Box>

          <AssessmentFooter
            fontSize={fontSize}
            setFontSize={setFontSize}
            isRecording={isRecording}
            isStopped={isStopped}
            audioBlob={audioBlob}
            showSlider={showSlider}
            setDuration={setDuration}
            onClickRetake={() => setOpenRetakeAudioDialog(true)}
            onClickAudioRecorder={onClickAudioRecorder}
            onClickSave={handleSaveClick}
            isProcessing={isProcessing}
            currentStep={currentStep}
          />
        </Box>
      </Box>

      <InstructionBackdrop
        openBackdrop={openBackdrop}
        setOpenBackdrop={setOpenBackdrop}
      />
      <RetakeAudioDialog
        isDialogOpen={openRetakeAudioDialog}
        setDialogOpen={setOpenRetakeAudioDialog}
        setShowSlider={setShowSlider}
        setAudioBlob={setAudioBlob}
        onClickCancel={() => setOpenRetakeAudioDialog(false)}
        onClickRetake={handleRetakeAudioRetake}
      />
      <LoadingDialog open={isQualityChecking} />
      <SaveAudioConfirmationDrawer
        open={openSaveAudioConfirmationDrawer}
        onOpenChange={setOpenSaveAudioConfirmationDrawer}
        audioBlob={audioBlob}
        audioBlobRef={audioBlobRef}
        setAudioBlobRef={setAudioBlobRef}
        audioQualityResult={qualityResult}
        onSave={handleSaveRecording}
        onRetake={handleRetakeRecording}
        currentStep={currentStep}
        audioUploading={audioUploading}
      />
      <ReadingDifficultyConfirmationDrawer
        openDrawer={openReadingDifficultyConfirmationDrawer}
        onClickPrimaryButton={handleReadingDifficultyConfirmationPrimary}
        onClickSecondaryButton={handleReadingDifficultyConfirmationSecondary}
        uploadDataLoading={allAssessmentUploadedLoading}
      />
      <AudioSizeShortError
        isDialogOpen={audioSizeShort}
        warningHeading={t("audio_size_small_heading")}
        warningMessage={t("audio_size_small_text")}
        onClickRetakeButton={handleAudioSizeErrorRetake}
      />
      <AudioSaveSuccessDialog
        isDialogOpen={audioSaveSuccessDialog}
        header={t("recording_saved_successfully")}
        description={getAudioSaveSuccessDialogDescription(currentStep, t)}
        btnText={getAudioSaveSuccessDialogBtnText(currentStep, t)}
        celebrationImage={getAudioSaveSuccessDialogImage(currentStep)}
        onClick={onClickAudioSaveSuccess}
      />
      <CustomErrorDialogBox
        header={t("something_went_wrong")}
        icon={
          <ErrorOutlineOutlinedIcon
            style={{
              color: "#D95959",
              borderRadius: "10px",
              marginTop: "16px",
              width: "80px",
              height: "80px",
            }}
          />
        }
        primaryButtonText={t("retake")}
        secondaryButtonText={t("new_student")}
        isDialogOpen={errorDialogOpen}
        showPrimaryButton
        showSecondaryButton
        onClickPrimaryButton={handleRetryClick}
        onClickSecondaryButton={() => sendMessageToReactNative("status=2")}
      />
    </>
  );
};

export default AssessmentScreen;
